/**
 * 远期房态：前端直调 PMS getAliasPriceQuantity + 房态「某日平均价」getAvgPriceByDate 取年同比/周环比 + 本地缓存（内存 2 分钟 + localStorage）
 * 直调失败时（如部署环境 CORS）回退到 GET /api/store-revenue/component?componentType=room-status-recently，保证价格/K 线等下半部分有数据
 */

import {
  getAliasPriceQuantityFromThirdParty,
  getAvgPricesByDates,
  getStoreRevenueComponent,
} from '../../../service/storeRevenueApi';
import type { RoomStatusRecentlyData } from '../../../service/storeRevenueApi';
import { aliasPriceQuantityToBaseChartData } from '../components/RoomStatusRecently/utils/roomStatusRecentlyChartTransformer';
import { getStoreFromLocalStorage, setStoreSliceInLocalStorage } from './storeRevenueLocalStorage';

/** Unix 秒 → YYYY-MM-DD */
function dateSecToYYYYMMDD(dateSec: number): string {
  const d = new Date(dateSec * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** 某日去年同日、以及七天前的日期（用于年同比/周环比折线） */
function toLastYearAndSevenDaysAgo(dateStr: string): { lastYear: string; sevenDaysAgo: string } {
  const d = new Date(dateStr + 'T12:00:00');
  const lastYear = new Date(d);
  lastYear.setFullYear(d.getFullYear() - 1);
  const seven = new Date(d);
  seven.setDate(d.getDate() - 7);
  const fmt = (x: Date) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return { lastYear: fmt(lastYear), sevenDaysAgo: fmt(seven) };
}

const TTL_MS = 2 * 60 * 1000; // 2 分钟

interface CacheEntry {
  data: RoomStatusRecentlyData['baseChartData'];
  fetchedAt: number;
}

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<RoomStatusRecentlyData['baseChartData']>>();

function cacheKey(hotelId: number | string): string {
  return `roomStatusRecently:${String(hotelId)}`;
}

function isValid(entry: CacheEntry): boolean {
  return Date.now() - entry.fetchedAt < TTL_MS;
}

function hasBaseChartDataShape(v: unknown): v is RoomStatusRecentlyData['baseChartData'] {
  if (v == null || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return 'categories' in o && 'dataset' in o;
}

/** 从 localStorage 恢复（若有且在 TTL 内）并写入内存缓存，供 hook 做初始渲染 */
export function getRoomStatusRecentlyFromLocalStorageSync(
  hotelId: number | string
): RoomStatusRecentlyData['baseChartData'] | null {
  const stored = getStoreFromLocalStorage(hotelId)?.roomStatusRecently;
  if (!stored?.data || typeof stored.fetchedAt !== 'number') return null;
  if (Date.now() - stored.fetchedAt > TTL_MS) return null;
  const inner = (stored.data as { baseChartData?: unknown })?.baseChartData;
  if (!hasBaseChartDataShape(inner)) return null;
  cache.set(cacheKey(hotelId), { data: inner, fetchedAt: stored.fetchedAt });
  return inner;
}

/**
 * 获取远期房态 baseChartData：优先内存缓存，其次 localStorage（同 TTL），再直调 getAliasPriceQuantity 并写缓存与 localStorage
 */
export async function getRoomStatusRecentlyWithCache(
  hotelId: number | string
): Promise<RoomStatusRecentlyData['baseChartData']> {
  const key = cacheKey(hotelId);
  let entry = cache.get(key);
  if (entry && isValid(entry)) {
    return entry.data;
  }
  const fromLocal = getRoomStatusRecentlyFromLocalStorageSync(hotelId);
  if (fromLocal != null) return fromLocal;

  const existing = inFlight.get(key);
  if (existing) return existing;

  const promise = (async (): Promise<RoomStatusRecentlyData['baseChartData']> => {
    try {
      const response = await getAliasPriceQuantityFromThirdParty(hotelId);
      const baseChartData = aliasPriceQuantityToBaseChartData(response);
      const dates = (response.data ?? []).map((d) => dateSecToYYYYMMDD(d.date));
      if (dates.length > 0) {
        const lastYearDates = dates.map((dateStr) => toLastYearAndSevenDaysAgo(dateStr).lastYear);
        const sevenDaysAgoDates = dates.map((dateStr) => toLastYearAndSevenDaysAgo(dateStr).sevenDaysAgo);
        try {
          const [yearOverYearPrices, weekOverWeekPrices] = await Promise.all([
            getAvgPricesByDates(hotelId, lastYearDates),
            getAvgPricesByDates(hotelId, sevenDaysAgoDates),
          ]);
          (baseChartData.dataset as Record<string, unknown>).yearOverYear = yearOverYearPrices;
          (baseChartData.dataset as Record<string, unknown>).weekOverWeek = weekOverWeekPrices;
          const comp = baseChartData.comparison as {
            yearOverYear?: { value: number[]; color?: string; label?: string };
            weekOverWeek?: { value: number[]; color?: string; label?: string };
          } | undefined;
          if (comp) {
            comp.yearOverYear = { ...(comp.yearOverYear && typeof comp.yearOverYear === 'object' && !Array.isArray(comp.yearOverYear) ? comp.yearOverYear : {}), value: yearOverYearPrices };
            comp.weekOverWeek = { ...(comp.weekOverWeek && typeof comp.weekOverWeek === 'object' && !Array.isArray(comp.weekOverWeek) ? comp.weekOverWeek : {}), value: weekOverWeekPrices };
          }
        } catch {
          // 年同比/周环比失败时保留空数组，图表仍显示房态与价格
        }
      }
      const fetchedAt = Date.now();
      cache.set(key, { data: baseChartData, fetchedAt });
      setStoreSliceInLocalStorage(hotelId, 'roomStatusRecently', {
        data: { baseChartData },
        fetchedAt,
      });
      return baseChartData;
    } catch (directErr) {
      // 直调失败（如部署环境 CORS、网络）时回退到后端 component 接口，保证远期房态含价格/K 线
      try {
        const slice = await getStoreRevenueComponent(hotelId, 'room-status-recently');
        const baseChartData = (slice as { roomStatusRecently?: { baseChartData?: unknown } })
          ?.roomStatusRecently?.baseChartData;
        if (baseChartData && hasBaseChartDataShape(baseChartData)) {
          const fetchedAt = Date.now();
          cache.set(key, { data: baseChartData, fetchedAt });
          setStoreSliceInLocalStorage(hotelId, 'roomStatusRecently', {
            data: { baseChartData },
            fetchedAt,
          });
          return baseChartData;
        }
      } catch (fallbackErr) {
        if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('[远期房态] 直调与后端回退均失败', { hotelId, direct: directErr, fallback: fallbackErr });
        }
      }
      throw directErr;
    }
  })().finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, promise);
  return promise;
}

export function clearRoomStatusRecentlyCache(hotelId: number | string): void {
  cache.delete(cacheKey(hotelId));
  inFlight.delete(cacheKey(hotelId));
}
